import { faDownload, faPlusCircle } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Table } from "antd";
import React, { useState } from "react";
import { DateTimeViewer, DefaultForm, EnumViewer } from "../../components";
import {
  AnnouncementCollectModel,
  AnnouncementErrorModel,
  AnnouncementModel,
  CollectorFsTypeEnum,
  CollectorInvestmentTypeEnum,
  CollectorMpTypeEnum,
} from "../../entities/announcement";
import {
  AnnouncementCategoryEnum,
  AnnouncementContainedObjectsEnum,
  AnnouncementErrorTypeEnum,
  AnnouncementLetterTypeEnum,
} from "../../entities/enums";
import { useGetCompaniesSelectFilterQuery } from "../../features/api/apiSlice";
import { getAnnouncementErrorClassName } from "./common";

interface AnnouncementCollectorProps {
  collectCallback: (data: AnnouncementCollectModel) => Promise<AnnouncementModel[]>;
}

export const AnnouncementCollector = ({ collectCallback }: AnnouncementCollectorProps) => {
  const [isCollecting, setIsCollecting] = useState(false);
  const [collectRes, setCollectRes] = useState<AnnouncementModel[]>([]);
  const [form] = Form.useForm<AnnouncementCollectModel>();
  const selectedCategory = Form.useWatch("category", form);

  let model: AnnouncementCollectModel = {
    symbols: [],
    category: AnnouncementCategoryEnum.None,
    letterType: AnnouncementLetterTypeEnum.None,
    mpType: CollectorMpTypeEnum.All,
    fsType: CollectorFsTypeEnum.All,
    investmentType: CollectorInvestmentTypeEnum.All,
    fromDate: "",
    toDate: "",
    forceUpdate: false,
  };

  const onFinish = async (values: any) => {
    let collectModel: AnnouncementCollectModel = {
      ...values,
    };

    setIsCollecting(true);

    let res = await collectCallback(collectModel);
    setCollectRes(res);

    setIsCollecting(false);
  };

  const downloadConsoleFilter = async () => {
    const values = await form.validateFields();
    const payload: AnnouncementCollectModel = {
      ...model,
      ...values,
      symbols: values.symbols ?? [],
    };

    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: "application/json;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = "codal-filter.json";
    document.body.appendChild(anchor);
    anchor.click();
    document.body.removeChild(anchor);
    URL.revokeObjectURL(url);
  };

  const columns = [
    {
      title: "عنوان",
      dataIndex: "title",
      key: "title",
      render: (value: string, announce: AnnouncementModel) => (
        <a href={announce.url} target="_blank" rel="noreferrer">
          {value}
        </a>
      ),
    },
    { title: "شماره پیگیری", dataIndex: "tracingNumber", key: "tracingNumber" },
    { title: "تاریخ انتشار", dataIndex: "publishDate", key: "publishDate", render: (value: string) => <DateTimeViewer value={value} mode="date" /> },
    {
      title: "شامل",
      dataIndex: "contained",
      key: "contained",
      render: (value: AnnouncementContainedObjectsEnum) => <EnumViewer type={AnnouncementContainedObjectsEnum} value={value} isFlag={true} />,
    },
    {
      title: "خطاها",
      dataIndex: "errors",
      key: "errors",
      render: (errors: AnnouncementErrorModel[], announce: AnnouncementModel) => (
        <React.Fragment>
          {errors.map((error) => (
            <div className="mb-3" key={error.relatedObject + "_" + error.errorType}>
              <EnumViewer type={AnnouncementContainedObjectsEnum} isFlag={true} value={error.relatedObject} />
              :&nbsp;
              <EnumViewer className={getAnnouncementErrorClassName(error.errorType)} type={AnnouncementErrorTypeEnum} value={error.errorType} />
            </div>
          ))}
        </React.Fragment>
      ),
    },
  ];

  return (
    <div className="lg:grid lg:grid-cols-2 xl:grid-cols-3 gap-8">
      <div>
        <DefaultForm onFinish={onFinish} layout="vertical" initialValues={model} form={form}>
          <DefaultForm.DataSelect
            name="symbols"
            placeholder="انتخاب نمادها"
            multiple={true}
            recordId={(x) => x.symbol}
            query={useGetCompaniesSelectFilterQuery}
            displayText={(x) => `${x.symbol} (${x.name})`}
          />
          <DefaultForm.EnumSelect name="investmentType" type={CollectorInvestmentTypeEnum} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            <DefaultForm.EnumSelect name="category" label="گروه اطلاعیه" type={AnnouncementCategoryEnum} />
            <DefaultForm.EnumSelect name="letterType" type={AnnouncementLetterTypeEnum} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            <DefaultForm.EnumSelect
              name="mpType"
              type={CollectorMpTypeEnum}
              disabled={selectedCategory !== AnnouncementCategoryEnum.None && selectedCategory !== AnnouncementCategoryEnum.MonthlyPerformanceReport}
            />
            <DefaultForm.EnumSelect
              name="fsType"
              type={CollectorFsTypeEnum}
              disabled={
                selectedCategory !== AnnouncementCategoryEnum.None &&
                selectedCategory !== AnnouncementCategoryEnum.AnnualInformationAndFinancialStatements
              }
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 ">
            <DefaultForm.DatePicker name="fromDate" allowClear={true} style={{ width: "100%" }} />
            <DefaultForm.DatePicker name="toDate" allowClear={true} style={{ width: "100%" }} />
          </div>
          <DefaultForm.Checkbox name="forceUpdate" label="بروزرسانی اجباری؟" />
          <div className="flex gap-3 flex-wrap">
            <DefaultForm.SubmitButton loading={isCollecting} icon={<FontAwesomeIcon className="anticon" icon={faPlusCircle} />}>
              کالکت
            </DefaultForm.SubmitButton>
            <button type="button" className="ant-btn" onClick={downloadConsoleFilter}>
              <FontAwesomeIcon className="anticon ml-2" icon={faDownload} />
              دانلود فیلتر کنسول
            </button>
          </div>
        </DefaultForm>
      </div>
      <div className="xl:col-span-2">
        {collectRes?.length > 0 && (
          <Table
            title={() => `نتیجه کالکت (تعداد ${collectRes.length})`}
            dataSource={collectRes}
            columns={columns}
            size="small"
            scroll={{ y: 256, x: true }}
            bordered={true}
            pagination={false}
          />
        )}
      </div>
    </div>
  );
};
